import logoUrl from '@field-support/design-tokens/assets/loynova-logo.png';
import { ChevronRight, Languages, List, LogOut, Menu, Pin, PinOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/entities/session';
import { setLanguage } from '@/shared/i18n';
import { NAV_ICONS } from './nav-icons';
import { NAV_ITEMS, type NavItem } from './navigation';
import { ApiStatus, EnvBadge, ZohoStatus } from './ShellStatus';
import { useRail } from './useRail';

const SECTIONS: NavItem['section'][] = ['overview', 'configuration', 'catalog', 'administration'];

/** An item whose path prefixes another item's (/users ⊃ /users/devices) matches exactly, so one item is active at a time. */
const isParent = (item: NavItem) => NAV_ITEMS.some((other) => other.path.startsWith(`${item.path}/`));

/** The nav item a path belongs to: the longest item path that prefixes it. */
const itemFor = (pathname: string) =>
  NAV_ITEMS.filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('');

/**
 * App shell (Tangram): the blue icon rail (hover or focus expands it, pin docks it, a drawer on
 * narrow screens) built from NAV_ITEMS (permission-filtered), and a top bar with where you are,
 * the environment and connection state, the language switch and who is signed in.
 */
export function Layout() {
  const { t, i18n } = useTranslation();
  const { user, can, canAny, logout } = useAuth();
  const { pathname } = useLocation();
  const rail = useRail();
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);

  const visible = NAV_ITEMS.filter((item) => item.permissions.length === 0 || canAny(...item.permissions));
  const current = itemFor(pathname);
  const expanded = rail.open || rail.pinned;
  const pinLabel = t(rail.pinned ? 'shell.unpin' : 'shell.pin');
  // On narrow screens the closed drawer is off-screen: inert, so Tab and screen readers skip its links.
  const drawerClosed = rail.compact && !rail.open;
  const railRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  // Opening the drawer moves focus into it; closing it (Escape, scrim) returns focus to the menu button.
  useEffect(() => {
    if (!rail.compact) return;
    if (rail.open) railRef.current?.querySelector<HTMLElement>('a[href], button')?.focus();
    else if (wasOpen.current && railRef.current?.contains(document.activeElement)) menuRef.current?.focus();
    wasOpen.current = rail.open;
  }, [rail.compact, rail.open]);

  // While collapsed the labels are hidden, so a hovered item names itself in a tooltip beside the rail.
  const showTip = (label: string) => (event: { currentTarget: HTMLElement }) => {
    if (expanded || rail.compact) return;
    const box = event.currentTarget.getBoundingClientRect();
    setTip({ label, top: box.top + box.height / 2 });
  };
  const hideTip = () => setTip(null);

  return (
    <div className="app-shell" data-open={rail.open} data-pinned={rail.pinned} data-compact={rail.compact}>
      <aside
        ref={railRef}
        className="app-rail"
        aria-label={t('common.appName')}
        inert={drawerClosed}
        {...rail.railProps}
        onMouseLeave={(event) => {
          hideTip();
          rail.railProps.onMouseLeave(event);
        }}
      >
        <div className="app-rail__head">
          {/* The mark as supplied, on its white tile (it never sits directly on blue). */}
          <span className="app-rail__mark">
            <img src={logoUrl} alt="Loynova" />
          </span>
          <span className="app-rail__product">
            <span className="app-rail__wordmark">Field Support</span>
            <span className="app-rail__app-name">{t('common.appName')}</span>
          </span>
        </div>
        <nav className="app-rail__nav" aria-label={t('common.primaryNav')}>
          {SECTIONS.map((section) => {
            const items = visible.filter((item) => item.section === section);
            if (items.length === 0) return null;
            return (
              <div key={section} className="app-rail__section">
                <div className="app-rail__section-title">
                  <span>{t(`nav.${section}`)}</span>
                </div>
                {items.map((item) => {
                  const Icon = NAV_ICONS[item.path] ?? List;
                  const label = t(item.labelKey);
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={isParent(item)}
                      className="app-rail__link"
                      onClick={rail.close}
                      onMouseEnter={showTip(label)}
                      onMouseLeave={hideTip}
                    >
                      <span className="app-rail__now" aria-hidden="true" />
                      <Icon className="app-rail__icon" aria-hidden="true" />
                      <span className="app-rail__label">{label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        {!rail.compact && (
          <div className="app-rail__foot">
            <button
              type="button"
              className="app-rail__link"
              aria-pressed={rail.pinned}
              aria-label={pinLabel}
              onClick={() => rail.setPinned(!rail.pinned)}
              onMouseEnter={showTip(pinLabel)}
              onMouseLeave={hideTip}
            >
              {rail.pinned ? (
                <PinOff className="app-rail__icon" aria-hidden="true" />
              ) : (
                <Pin className="app-rail__icon" aria-hidden="true" />
              )}
              <span className="app-rail__label">{pinLabel}</span>
            </button>
          </div>
        )}
      </aside>
      {tip && !expanded && !rail.compact && (
        <div className="app-rail__tip" aria-hidden="true" style={{ insetBlockStart: tip.top }}>
          {tip.label}
        </div>
      )}
      <div className="app-scrim" aria-hidden="true" onClick={rail.close} />
      <div className="app-body">
        <header className="app-top">
          {rail.compact && (
            <button
              ref={menuRef}
              type="button"
              className="app-icon-button"
              aria-label={t('shell.menu')}
              aria-expanded={rail.open}
              onClick={rail.toggleDrawer}
            >
              <Menu aria-hidden="true" />
            </button>
          )}
          {current && (
            <div className="app-top__crumbs">
              <span>{t(`nav.${current.section}`)}</span>
              <ChevronRight className="app-top__crumb-sep" aria-hidden="true" />
              <b>{t(current.labelKey)}</b>
            </div>
          )}
          <span className="fs-spacer" />
          {/* What the old top bar carried: environment and connection status. */}
          <div className="app-top__status">
            <EnvBadge />
            <ApiStatus />
            {can('manageZoho') && <ZohoStatus />}
          </div>
          <button type="button" className="app-top__language" onClick={() => setLanguage(i18n.language === 'ar' ? 'en' : 'ar')}>
            <Languages aria-hidden="true" />
            {t('common.language')}
          </button>
          {user && (
            <div className="app-top__user">
              <span className="app-top__avatar" aria-hidden="true">
                {initials(user.fullName)}
              </span>
              <span className="app-top__user-text">
                <span className="app-top__user-name">{user.fullName}</span>
                <span className="app-top__user-role">{t(`users.roles.${user.role}`)}</span>
              </span>
            </div>
          )}
          <button
            type="button"
            className="app-icon-button"
            aria-label={t('common.logout')}
            title={t('common.logout')}
            onClick={() => void logout()}
          >
            <LogOut className="app-icon-button__flip" aria-hidden="true" />
          </button>
        </header>
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
